import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import { FileDown, FileText } from "lucide-react";
import {
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Select,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Wrap,
  useToast,
} from "@chakra-ui/react";
import { useAuth } from "../../services/auth/AuthProvider";
import { MultiSelect } from "../../components/MultiSelect";
import { DateRangePicker } from "../statistics/DateRangePicker";
import { type Period, rangeFor } from "../statistics/period";
import { getInvoices, invoiceMs } from "../../services/api/invoices";
import { getSpecificBooks } from "../../services/api/books";
import { getPublishers } from "../../services/api/publishers";
import type { BalanceRow, ReportLabels } from "./balanceReport";
import Seo from "../../../../ui/components/Seo/Seo";

const TODAY = new Date().toISOString().slice(0, 10);
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
const BOOK_CHUNK_SIZE = 50;

const PLATFORM_OPTIONS = [
  { value: "iOS", label: "iOS" },
  { value: "Android", label: "Android" },
  { value: "Web", label: "Web" },
];

interface Applied {
  startDate: string;
  endDate: string;
  startMs: number;
  endMs: number;
  publishersParam?: string;
  headerPublishers: string;
}

export function BalancePage() {
  const toast = useToast();
  const { publishingHouse, isSuperadmin, firebaseUser } = useAuth();
  const ownName = publishingHouse?.name ?? "";
  const ownPct = publishingHouse?.revenuePercent ?? 1;

  const [period, setPeriod] = useState<Period>("currentMonth");
  const [customStart, setCustomStart] = useState(`${TODAY.slice(0, 7)}-01`);
  const [customEnd, setCustomEnd] = useState(TODAY);
  const [selectedPublishers, setSelectedPublishers] = useState<string[]>([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [applied, setApplied] = useState<Applied | null>(null);

  const publishersQuery = useQuery(["publishers"], getPublishers, { enabled: isSuperadmin });
  const allPublishers = useMemo(() => publishersQuery.data ?? [], [publishersQuery.data]);
  const pctByName = useMemo(() => {
    const m: Record<string, number> = {};
    if (isSuperadmin) allPublishers.forEach((p) => (m[p.name] = p.revenuePercent));
    else m[ownName] = ownPct;
    return m;
  }, [isSuperadmin, allPublishers, ownName, ownPct]);

  const invoicesQuery = useQuery(
    ["balance-invoices", applied?.startDate, applied?.endDate, applied?.publishersParam],
    () =>
      getInvoices({
        startDate: applied!.startDate,
        endDate: applied!.endDate,
        publishers: applied?.publishersParam,
      }),
    { enabled: !!applied, keepPreviousData: true },
  );

  const bookMapRef = useRef<Record<string, { name: string; isbn: string; authors: string }>>({});
  const [bookMapVersion, setBookMapVersion] = useState(0);
  const [booksReady, setBooksReady] = useState(false);
  const [firstBatchLoaded, setFirstBatchLoaded] = useState(false);
  const loadIdRef = useRef(0);

  useEffect(() => {
    if (invoicesQuery.isFetching) {
      setFirstBatchLoaded(false);
    }
  }, [invoicesQuery.isFetching]);

  const loadAllBooks = useCallback(
      async (invoices: typeof invoicesQuery.data) => {
        if (!firebaseUser || !invoices) {
          setFirstBatchLoaded(true);
          return;
        }

        const currentLoadId = ++loadIdRef.current;

        bookMapRef.current = {};
        setBookMapVersion(0);
        setBooksReady(false);
        setFirstBatchLoaded(false);

        const uniqueIds = [...new Set(invoices.map((inv) => inv.bookId).filter(Boolean))];

        if (uniqueIds.length === 0) {
          setFirstBatchLoaded(true);
          setBooksReady(true);
          return;
        }

        try {
          for (let i = 0; i < uniqueIds.length; i += BOOK_CHUNK_SIZE) {
            const chunk = uniqueIds.slice(i, i + BOOK_CHUNK_SIZE);
            const books = await getSpecificBooks(firebaseUser.uid, chunk);

            if (loadIdRef.current !== currentLoadId) return;

            books.forEach((b) => {
              bookMapRef.current[b.id] = {
                name: b.name || b.id,
                isbn: b.isbn ?? "",
                authors: Array.isArray(b.authors) ? b.authors.map(String).join(", ") : "",
              };
            });
            setBookMapVersion((v) => v + 1);

            if (i === 0) {
              setFirstBatchLoaded(true);
            }
          }

          if (loadIdRef.current === currentLoadId) {
            setBooksReady(true);
          }
        } catch (err) {
          if (loadIdRef.current === currentLoadId) {
            setFirstBatchLoaded(true);
            setBooksReady(true);
          }
        }
      },
      [firebaseUser],
  );

  useEffect(() => {
    if (invoicesQuery.data) {
      loadAllBooks(invoicesQuery.data);
    }
  }, [invoicesQuery.data, loadAllBooks]);

  const allRows = useMemo<BalanceRow[]>(() => {
    const invoices = invoicesQuery.data ?? [];
    return invoices.map((inv) => {
      const book = bookMapRef.current[inv.bookId];
      const amount = Number(inv.amount) || 0;
      const pct = pctByName[inv.publishingHouse ?? ""] ?? (isSuperadmin ? 1 : ownPct);
      return {
        transactionMs: invoiceMs(inv.updateDate),
        bookName: book?.name ?? inv.bookId,
        isbn: book?.isbn ?? "",
        authors: book?.authors ?? "",
        amount,
        revenue: amount * pct,
        platform: inv.platform ?? "",
        publishingHouse: inv.publishingHouse ?? "",
      };
    });
  }, [invoicesQuery.data, bookMapVersion, pctByName, isSuperadmin, ownPct]);

  const rows = useMemo(
    () =>
      selectedPlatforms.length === 0
        ? allRows
        : allRows.filter((r) => selectedPlatforms.includes(r.platform)),
    [allRows, selectedPlatforms],
  );

  const total = useMemo(() => rows.reduce((s, r) => s + r.revenue, 0), [rows]);

  function showResults() {
    if (period === "custom" && customStart > customEnd) {
      toast({ description: "Невірний діапазон дат", status: "error" });
      return;
    }
    const range = rangeFor(period, customStart, customEnd);
    const headerPublishers = selectedPublishers.length
      ? selectedPublishers.join(", ")
      : isSuperadmin
        ? "Всі видавництва"
        : ownName;
    setSelectedPlatforms([]);
    setFirstBatchLoaded(false);
    setApplied({
      startDate: range.startIso,
      endDate: range.endIso,
      startMs: new Date(range.startIso).getTime(),
      endMs: new Date(range.endIso).getTime(),
      publishersParam: isSuperadmin
        ? selectedPublishers.length
          ? selectedPublishers.join(",")
          : undefined
        : ownName,
      headerPublishers,
    });
  }

  const reportLabels: ReportLabels = {
    report: "Звіт",
    salesReport: "Звіт продажів",
    isbn: "ISBN",
    name: "Назва",
    authors: "Автор",
    price: "Фактична ціна продажу книжки (грн)",
    count: "Кількість",
    royalty: "Роялті",
    total: "Всього",
    transactionDate: "Дата транзакції",
    bookName: "Книжка",
    revenue: "Виручка видавництва (грн)",
    sumForPayment: "Сума до виплати",
  };

  async function exportXlsx() {
    const mod = await import("./balanceReport");
    mod.downloadXlsx(rows, total, reportLabels);
  }
  async function exportPdf() {
    const mod = await import("./balanceReport");
    await mod.downloadPdf(
        rows,
        total,
        {
          publishers: applied?.headerPublishers ?? "",
          startMs: applied?.startMs ?? 0,
          endMs: applied?.endMs ?? 0,
        },
        reportLabels,
    );
  }

  const loading =
      invoicesQuery.isLoading ||
      invoicesQuery.isFetching ||
      (!firstBatchLoaded && !invoicesQuery.isError && !!invoicesQuery.data);

  const hasResults = !!applied && !loading && rows.length > 0;
  const exportReady = hasResults && booksReady;

  return (
    <Flex direction="column" h="100%" overflow="hidden">
      <Seo
        title="Баланс — Кабінет видавця"
        description="Фінансовий баланс та звіти видавництва"
        noindex
      />
      <Flex
        as="header"
        flex="0 0 auto"
        align="center"
        bg="white"
        borderBottom="1px solid"
        borderColor="blackAlpha.100"
        px={{ base: 4, md: 6 }}
        py={{ base: 3, md: 4 }}
      >
        <Heading
          size={{ base: "md", md: "lg" }}
          fontWeight="medium"
          letterSpacing="wide"
          textTransform="uppercase"
        >
          {"Баланс"}
        </Heading>
      </Flex>

      <Box flex="0 0 auto" px={{ base: 4, md: 6 }} pt={{ base: 4, md: 6 }} pb={4}>
        <Wrap spacing={3} align="flex-end">
          <FormControl w="fit-content">
            <FormLabel fontSize="xs">{"Період"}</FormLabel>
            <Select value={period} onChange={(e) => setPeriod(e.target.value as Period)} size="sm">
              <option value="currentMonth">{"Поточний місяць"}</option>
              <option value="previousMonth">{"Попередній місяць"}</option>
              <option value="currentYear">{"Поточний рік"}</option>
              <option value="previousYear">{"Попередній рік"}</option>
              <option value="custom">{"Інша дата"}</option>
            </Select>
          </FormControl>

          {period === "custom" && (
            <FormControl w="fit-content">
              <FormLabel fontSize="xs">{"Інша дата"}</FormLabel>
              <DateRangePicker
                start={customStart}
                end={customEnd}
                placeholder={"Обрати діапазон"}
                onChange={(s, e) => {
                  setCustomStart(s);
                  setCustomEnd(e);
                }}
              />
            </FormControl>
          )}

          {isSuperadmin && (
            <FormControl w="fit-content">
              <FormLabel fontSize="xs">{"Видавництва"}</FormLabel>
              <MultiSelect
                label={"Видавництва"}
                options={allPublishers.map((p) => ({ value: p.name, label: p.name }))}
                value={selectedPublishers}
                onChange={setSelectedPublishers}
                searchPlaceholder={"Пошук"}
                minW="220px"
              />
            </FormControl>
          )}

          <FormControl w="fit-content">
            <FormLabel fontSize="xs">{"Платформи"}</FormLabel>
            <MultiSelect
              label={"Платформи"}
              options={PLATFORM_OPTIONS}
              value={selectedPlatforms}
              onChange={setSelectedPlatforms}
            />
          </FormControl>

          <Button
            onClick={showResults}
            isLoading={loading}
            isDisabled={isSuperadmin && publishersQuery.isLoading}
          >
            {"Показати результати"}
          </Button>
        </Wrap>
      </Box>

      {!applied ? (
        <Center flex="1">
          <Text color="gray.500">{"Показати результати"}</Text>
        </Center>
      ) : loading ? (
        <Center flex="1">
          <Spinner color="brand.orange" />
        </Center>
      ) : rows.length === 0 ? (
        <Center flex="1">
          <Text color="gray.500">{"Немає результатів"}</Text>
        </Center>
      ) : (
        <>
          {/* Payout total + export — pinned */}
          <Box flex="0 0 auto" px={{ base: 4, md: 6 }} pb={4}>
            <Flex
              align="center"
              justify="space-between"
              wrap="wrap"
              gap={4}
              bg="white"
              border="1px solid"
              borderColor="blackAlpha.100"
              borderRadius="lg"
              boxShadow="sm"
              p={4}
            >
              <Stat flex="0 0 auto">
                <StatLabel color="gray.500">{"Сума до виплати"}</StatLabel>
                <StatNumber color="brand.orange">{round2(total)} грн.</StatNumber>
              </Stat>
              <Flex gap={3} flexWrap="wrap" w={{ base: "full", md: "auto" }}>
                <Button
                  variant="outline"
                  flex={{ base: "1 1 8rem", md: "0 0 auto" }}
                  leftIcon={<Icon as={FileDown} boxSize={4} />}
                  onClick={exportXlsx}
                  isDisabled={!exportReady}
                >
                  {"Звіт XLSX"}
                </Button>
                <Button
                  variant="outline"
                  flex={{ base: "1 1 8rem", md: "0 0 auto" }}
                  leftIcon={<Icon as={FileText} boxSize={4} />}
                  onClick={exportPdf}
                  isDisabled={!exportReady}
                >
                  {"Звіт PDF"}
                </Button>
              </Flex>
            </Flex>
          </Box>

          {/* Only the table scrolls */}
          <Box
            flex="1 1 auto"
            minH={0}
            overflowY="auto"
            overflowX="auto"
            mx={{ base: 4, md: 6 }}
            mb={{ base: 4, md: 6 }}
            bg="white"
            border="1px solid"
            borderColor="blackAlpha.100"
            borderRadius="lg"
            boxShadow="sm"
          >
            <Table size="sm" variant="simple">
              <Thead position="sticky" top={0} zIndex={1} bg="gray.50">
                <Tr>
                  <Th whiteSpace="normal">{"Дата транзакції"}</Th>
                  <Th whiteSpace="normal">{"Книжка"}</Th>
                  <Th isNumeric whiteSpace="normal">
                    {"Фактична ціна продажу книжки (грн)"}
                  </Th>
                  <Th isNumeric whiteSpace="normal">
                    {"Виручка видавництва (грн)"}
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((r, i) => (
                  <Tr key={i}>
                    <Td whiteSpace="nowrap">
                      {r.transactionMs
                        ? new Date(r.transactionMs).toLocaleDateString("uk-UA")
                        : "—"}
                    </Td>
                    <Td>{r.bookName}</Td>
                    <Td isNumeric>{r.amount}</Td>
                    <Td isNumeric>{round2(r.revenue)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </>
      )}
    </Flex>
  );
}